import React, { useState, useEffect } from 'react';
import api from '../../services/api';

export default function Informes() {
  const now = new Date();
  const [trabajadores, setTrabajadores] = useState([]);
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());
  const [selectedId, setSelectedId] = useState('todos');
  const [downloading, setDownloading] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get('/trabajadores').then(r => setTrabajadores(r.data.trabajadores.filter(t => t.rol === 'trabajador')));
  }, []);

  const showMsg = (tipo, texto) => {
    setMsg({ tipo, texto });
    setTimeout(() => setMsg(null), 4000);
  };

  const downloadFile = async (url, filename, tipo) => {
    setDownloading(tipo);
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const href = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(href);
      showMsg('ok', `Informe descargado: ${filename}`);
    } catch {
      showMsg('err', 'Error al generar el informe. Inténtalo de nuevo.');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownload = (formato) => {
    const mesStr = String(mes).padStart(2, '0');
    const params = `mes=${mes}&anio=${anio}`;

    if (selectedId === 'todos') {
      if (formato === 'excel') {
        downloadFile(`/exports/excel/plantilla?${params}`, `plantilla_${anio}_${mesStr}.xlsx`, 'excel-plantilla');
      }
    } else {
      const t = trabajadores.find(t => String(t.id) === String(selectedId));
      const nombre = t ? `${t.username}` : selectedId;
      if (formato === 'excel') {
        downloadFile(`/exports/excel/trabajador/${selectedId}?${params}`, `jornada_${nombre}_${anio}_${mesStr}.xlsx`, `excel-${selectedId}`);
      } else if (formato === 'pdf') {
        downloadFile(`/exports/pdf/trabajador/${selectedId}?${params}`, `jornada_${nombre}_${anio}_${mesStr}.pdf`, `pdf-${selectedId}`);
      }
    }
  };

  const [guardandoLocal, setGuardandoLocal] = useState(false);
  const soportaSelectorCarpeta = typeof window !== 'undefined' && !!window.showDirectoryPicker;

  // Guarda el Excel de plantilla directamente en una carpeta del PC del usuario,
  // dejándole elegir cuál cada vez mediante el selector nativo del navegador
  // (File System Access API). Si el navegador no lo soporta, cae a una
  // descarga normal (el usuario elige la carpeta desde el diálogo "Guardar como").
  const handleGuardarLocal = async () => {
    const mesStr = String(mes).padStart(2, '0');
    const filename = `plantilla_${anio}_${mesStr}.xlsx`;

    if (!soportaSelectorCarpeta) {
      downloadFile(`/exports/excel/plantilla?mes=${mes}&anio=${anio}`, filename, 'excel-plantilla');
      return;
    }

    setGuardandoLocal(true);
    try {
      const res = await api.get(`/exports/excel/plantilla?mes=${mes}&anio=${anio}`, { responseType: 'blob' });
      const dirHandle = await window.showDirectoryPicker();
      const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(res.data);
      await writable.close();
      showMsg('ok', `Copia guardada correctamente: ${filename}`);
    } catch (err) {
      if (err?.name !== 'AbortError') { // el usuario cerró el selector de carpeta sin elegir
        showMsg('err', 'No se pudo guardar la copia en el PC.');
      }
    } finally {
      setGuardandoLocal(false);
    }
  };

  const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  return (
    <div className="space-y-5">
      <div className="card space-y-4">
        <h3 className="font-semibold text-gray-800">Generar Informes de Jornada</h3>
        <p className="text-sm text-gray-500">
          Exporta los registros conforme al RD-ley 8/2019. Los documentos incluyen todos los fichajes del período seleccionado.
        </p>

        {/* Período */}
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="label">Mes</label>
            <select className="input" value={mes} onChange={e => setMes(Number(e.target.value))}>
              {meses.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div className="w-28">
            <label className="label">Año</label>
            <select className="input" value={anio} onChange={e => setAnio(Number(e.target.value))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {/* Trabajador */}
        <div>
          <label className="label">Trabajador</label>
          <select className="input" value={selectedId} onChange={e => setSelectedId(e.target.value)}>
            <option value="todos">— Toda la plantilla —</option>
            {trabajadores.map(t => (
              <option key={t.id} value={t.id}>{t.apellidos}, {t.nombre}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Mensaje */}
      {msg && (
        <div className={`px-4 py-3 rounded-xl text-sm font-medium
          ${msg.tipo === 'ok' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {msg.texto}
        </div>
      )}

      {/* Opciones de descarga */}
      <div className="space-y-3">
        {/* Excel */}
        <div className="card flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
              📊
            </div>
            <div>
              <div className="font-semibold text-gray-800">Excel (.xlsx)</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {selectedId === 'todos'
                  ? 'Resumen de plantilla + hoja individual por trabajador'
                  : 'Jornada detallada por días con totales'}
              </div>
            </div>
          </div>
          <button
            onClick={() => handleDownload('excel')}
            disabled={!!downloading}
            className="btn-primary flex-shrink-0 flex items-center gap-2"
          >
            {downloading?.includes('excel') ? (
              <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Generando...</>
            ) : (
              <><span>↓</span> Descargar</>
            )}
          </button>
        </div>

        {/* PDF — solo trabajador individual */}
        {selectedId !== 'todos' && (
          <div className="card flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                📄
              </div>
              <div>
                <div className="font-semibold text-gray-800">PDF</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Informe formal con membrete MGH, válido para inspección de trabajo
                </div>
              </div>
            </div>
            <button
              onClick={() => handleDownload('pdf')}
              disabled={!!downloading}
              className="btn-primary flex-shrink-0 flex items-center gap-2"
            >
              {downloading?.includes('pdf') ? (
                <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Generando...</>
              ) : (
                <><span>↓</span> Descargar</>
              )}
            </button>
          </div>
        )}

        {selectedId === 'todos' && (
          <>
            <div className="card bg-blue-50 border border-blue-100">
              <p className="text-sm text-blue-700">
                <span className="font-semibold">ℹ Para plantilla completa</span>, el Excel incluye una hoja de resumen más una hoja individual por cada trabajador.
                Para PDF individual, selecciona un trabajador específico.
              </p>
            </div>

            {/* Guardar copia en el PC / carpeta sincronizada con Drive */}
            <div className="card flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                  💾
                </div>
                <div>
                  <div className="font-semibold text-gray-800">Guardar copia en el PC</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {soportaSelectorCarpeta
                      ? 'Te pedirá elegir la carpeta de tu ordenador donde guardarlo (puedes elegir una distinta cada vez, por ejemplo una sincronizada con Drive/OneDrive).'
                      : 'Tu navegador no soporta elegir carpeta directamente; se descargará como un archivo normal.'}
                  </div>
                </div>
              </div>
              <button
                onClick={handleGuardarLocal}
                disabled={guardandoLocal}
                className="btn-primary flex-shrink-0 flex items-center gap-2"
              >
                {guardandoLocal ? (
                  <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Guardando...</>
                ) : (
                  <><span>💾</span> Guardar</>
                )}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Nota legal */}
      <div className="card bg-amber-50 border border-amber-100">
        <div className="flex gap-3">
          <span className="text-xl">⚖️</span>
          <div>
            <div className="font-semibold text-amber-900 text-sm">Obligación Legal</div>
            <div className="text-xs text-amber-700 mt-1">
              Conforme al <strong>Real Decreto-ley 8/2019</strong>, los registros de jornada deben conservarse durante
              <strong> 4 años</strong> y estar disponibles para los trabajadores, sus representantes y la Inspección de Trabajo.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
